import * as pdfjsLib from 'pdfjs-dist'

// Use local worker file from public folder
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

export interface ParsedPdfPage {
  pageNumber: number
  text: string
}

export interface ParsedPdf {
  pages: ParsedPdfPage[]
  fullText: string
  pageCount: number
}

export async function parsePdf(file: File): Promise<ParsedPdf> {
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({
    data: arrayBuffer,
    useSystemFonts: true,
    // Disable font loading to avoid font-related issues
    disableFontFace: true,
    // Normalize whitespace
    standardFontDataUrl: undefined,
  }).promise

  const pages: ParsedPdfPage[] = []
  let fullText = ''

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const textContent = await page.getTextContent()

    // Extract text with better handling of spacing
    let lastY: number | null = null
    let pageText = ''

    for (const item of textContent.items) {
      if ('str' in item && item.str) {
        // Check if we need a newline (different Y position)
        if (lastY !== null && 'transform' in item) {
          const currentY = item.transform[5]
          if (Math.abs(currentY - lastY) > 5) {
            pageText += '\n'
          } else {
            // Add space between items on same line
            pageText += ' '
          }
        }

        pageText += item.str

        if ('transform' in item) {
          lastY = item.transform[5]
        }
      }
    }

    // Clean up multiple spaces and normalize
    pageText = pageText
      .replace(/\s+/g, ' ')
      .replace(/\n\s+/g, '\n')
      .trim()

    pages.push({
      pageNumber: i,
      text: pageText,
    })

    fullText += pageText + '\n\n'
  }

  const result = {
    pages,
    fullText: fullText.trim(),
    pageCount: pdf.numPages,
  }

  // Log warning if no text extracted
  if (!result.fullText) {
    console.warn('PDF parsing returned empty text. The PDF might be image-based or have encrypted text.')
  }

  return result
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Remove data URL prefix
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
